import React, { useRef } from 'react';
import { Icons } from '@ohif/ui-next';

interface FileUploadProps {
  label: string;
  value?: string;
  onChange: (file: File | null) => void;
  accept?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  /** If this returns a string, the file is rejected, the input is cleared, and `onValidationError` runs. */
  validateSelectedFile?: (file: File) => string | undefined;
  onValidationError?: (message: string) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  value,
  onChange,
  accept = 'image/*',
  disabled = false,
  required = false,
  error,
  validateSelectedFile,
  onValidationError,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file && validateSelectedFile) {
      const validationMessage = validateSelectedFile(file);
      if (validationMessage) {
        e.target.value = '';
        onChange(null);
        onValidationError?.(validationMessage);
        return;
      }
    }
    onChange(file);
  };

  const handleUploadClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const truncateFileName = (fileName: string | undefined): string => {
    if (!fileName || fileName === 'No file selected') {
      return fileName || 'No file selected';
    }
    if (fileName.length > 16) {
      return fileName.substring(0, 16) + '...';
    }
    return fileName;
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
      }}
    >
      {/* Label */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          position: 'relative',
        }}
      >
        <label
          style={{
            fontSize: '14px',
            fontWeight: 500,
            color: disabled ? 'var(--label-color-disabled)' : 'var(--label-color-default)',
            lineHeight: '20px',
          }}
        >
          {label}
        </label>
        {required && (
          <span
            style={{
              color: '#dc2626',
              fontSize: '10px',
              position: 'absolute',
              left: '-8px',
              top: '2px',
              fontWeight: 600,
            }}
          >
            *
          </span>
        )}
      </div>

      {/* File Upload Field */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          alignItems: 'flex-start',
        }}
      >
        {/* File Name Display */}
        <div
          style={{
            flex: 1,
            height: '36px',
            padding: '8px 12px',
            backgroundColor: disabled ? 'var(--bg-input-disabled)' : 'var(--bg-input)',
            border: `1px solid ${disabled ? 'var(--border-input-disabled)' : 'var(--border-input)'}`,
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 400,
            color: disabled ? 'var(--text-input-disabled)' : 'var(--text-input-special)',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
            title={value || 'No file selected'}
          >
            {truncateFileName(value)}
          </span>
        </div>

        {/* Upload Button */}
        <button
          type="button"
          onClick={handleUploadClick}
          disabled={disabled}
          style={{
            height: '36px',
            padding: '0 12px',
            backgroundColor: disabled ? 'var(--bg-input-disabled)' : 'var(--bg-button-upload)',
            border: `1px solid ${disabled ? 'var(--border-input-disabled)' : 'var(--border-button-upload)'}`,
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 500,
            color: disabled ? 'var(--text-input-disabled)' : 'var(--text-input-default)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            outline: 'none',
            minWidth: 'fit-content',
            boxSizing: 'border-box',
            opacity: disabled ? 0.6 : 1,
          }}
          onMouseEnter={e => {
            if (!disabled) {
              e.currentTarget.style.backgroundColor = '#0f172a';
            }
          }}
          onMouseLeave={e => {
            if (!disabled) {
              e.currentTarget.style.backgroundColor = 'var(--bg-button-upload)';
            }
          }}
        >
          <Icons.UploadCustom
            width="16"
            height="16"
          />
          <span>Upload</span>
        </button>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {/* Error Message */}
      {error && (
        <span
          style={{
            fontSize: '12px',
            color: '#dc2626',
            marginTop: '4px',
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default FileUpload;
